/**
 * May the Overview show the Building (3D) view here, and if not, why not
 * (twin slice 1, plan W4 step 7; catalogue §8, resolution step 3)?
 *
 * When it may not, the Overview shows the Map with the SAME focus and one
 * line saying why. The reasons are checked in a fixed order and the first
 * that applies is the one given:
 *
 *   1. flag-off   `featureFlags.twinEnabled` is not on for this console.
 *   2. role       none of the person's roles is in `BUILDING_VIEW_ROLES`.
 *   3. no-asset   there is no building model for this site. The registry
 *                 is W1's `twinAssetFor`; this module takes its answer as
 *                 `hasAsset` so it does not load the asset to ask.
 *   4. narrow     the screen is under 768 px wide.
 *   5. no-webgl   the canvas reported `unsupported`: no WebGL 2 context
 *                 could be made on this device.
 *   6. stuck      the canvas reported `stuck`: it lost its context and it
 *                 did not come back (`twin-gl.ts`).
 *
 * A transient `lost` does NOT fall back. An iPad drops the context when
 * it frees memory and usually hands it back by itself; sending the person
 * to the Map on every loss would move their screen for something the 3D
 * view is already saying ("paused while the device frees graphics
 * memory"). Only `stuck` -- lost for 8 s of visible time, or a Try again
 * that failed -- is a reason. `restored` clears nothing here because
 * nothing was set: the host keeps the LAST report, and a restore replaces
 * a `lost` the same way it would replace no report at all.
 *
 * The order is the plan's (W4 step 7). One consequence: a phone that also
 * has no WebGL is told it is too narrow.
 *
 * The words are one line each, in the hotel's register: no "WebGL", no
 * flag names, no role names. They are copy for review (plan gate B).
 *
 * `GraphicsState` is a TYPE import from `twin-gl.ts`, erased at build: a
 * value import would pull three.js into the main bundle.
 */
import { BUILDING_VIEW_ROLES, hasAnyRole, type RoleLike } from '../auth/roles';
import type { GraphicsState } from '../screens/Twin/twin-gl';

/** Screens narrower than this get the Map. 768 itself is wide enough. */
export const BUILDING_VIEW_MIN_WIDTH_PX = 768;

export type TwinUnavailableReason =
  | 'flag-off'
  | 'role'
  | 'no-asset'
  | 'narrow'
  | 'no-webgl'
  | 'stuck';

/** Every reason, in the order they are checked. */
export const TWIN_UNAVAILABLE_REASONS: readonly TwinUnavailableReason[] = [
  'flag-off',
  'role',
  'no-asset',
  'narrow',
  'no-webgl',
  'stuck',
];

/** The one line the Map shows when it stands in for the Building view. */
export const TWIN_UNAVAILABLE_TEXT: Readonly<Record<TwinUnavailableReason, string>> = {
  'flag-off': 'The building view is switched off on this console. Showing the map.',
  role: "Your account doesn't include the building view. Showing the map.",
  'no-asset': "There's no building model for this site yet. Showing the map.",
  narrow: 'This screen is too narrow for the building view. Showing the map.',
  'no-webgl': "This device can't draw the 3D building. Showing the map.",
  stuck: "The building view stopped drawing and hasn't come back. Showing the map.",
};

export interface TwinAvailabilityInputs {
  /** `getRuntimeConfig().featureFlags.twinEnabled`; absent means off. */
  readonly twinEnabled: boolean | null | undefined;
  /** The person's roles, in either spelling (`roles.ts`). */
  readonly roles: Iterable<RoleLike> | null | undefined;
  /** Whether `twinAssetFor(site)` has a building model for this site. */
  readonly hasAsset: boolean;
  /**
   * The screen's width in CSS px, as the host measures it. Null (or a
   * non-finite number) when it could not be measured, which skips this one
   * check rather than guessing: the graphics reports still catch a device
   * that cannot draw.
   */
  readonly screenWidthPx: number | null;
  /** The canvas's LAST `onGraphicsState` report; null before any. */
  readonly graphics: GraphicsState | null;
}

export type TwinAvailability =
  | { readonly available: true }
  | { readonly available: false; readonly reason: TwinUnavailableReason; readonly text: string };

const AVAILABLE: TwinAvailability = { available: true };

function unavailable(reason: TwinUnavailableReason): TwinAvailability {
  return { available: false, reason, text: TWIN_UNAVAILABLE_TEXT[reason] };
}

/** Pure: the first reason that applies, or available. */
export function twinAvailability(inputs: TwinAvailabilityInputs): TwinAvailability {
  if (inputs.twinEnabled !== true) return unavailable('flag-off');
  if (!hasAnyRole(inputs.roles, BUILDING_VIEW_ROLES)) return unavailable('role');
  if (!inputs.hasAsset) return unavailable('no-asset');
  const width = inputs.screenWidthPx;
  if (width !== null && Number.isFinite(width) && width < BUILDING_VIEW_MIN_WIDTH_PX) {
    return unavailable('narrow');
  }
  if (inputs.graphics === 'unsupported') return unavailable('no-webgl');
  if (inputs.graphics === 'stuck') return unavailable('stuck');
  // 'lost' is transient (see the header); 'restored' and no report are fine.
  return AVAILABLE;
}
